import { useEffect, useId, useState, type ReactNode, type RefObject } from 'react'
import * as posta from '../../servizi/posta.ts'
import '../../stile-posta-caselle.css'

/**
 * Suggerimenti della rubrica nei campi A, Cc e Ccn del compositore (527, decisioni di Davide del
 * 2/11/2026): mentre si scrive un destinatario compaiono i contatti della casella da cui si scrive e i
 * colleghi di CIDIS che cominciano con quelle lettere. Frecce per scorrere, Invio per scegliere, Esc per
 * chiudere; il campo e' una combobox con l'elenco annunciato.
 *
 * Il campo non e' governato da React (bozze condivise, 505): la scelta scrive il valore nel campo e
 * passa da `cambia`, come una battuta, cosi' entra nel documento condiviso come ogni altra modifica.
 *
 * Gruppi della rubrica (539): si propongono per nome, prima dei contatti; scelto, il gruppo mette tutti i suoi
 * indirizzi. Un nome di gruppo scritto per intero diventa i suoi indirizzi quando si esce dal campo.
 */

/** Rubrica e colleghi della casella, da proporre; vuoti finche' non arrivano o se non si leggono. */
export function useSuggerimenti(casellaId: string | null | undefined): posta.Suggerimento[] {
  const [voci, setVoci] = useState<posta.Suggerimento[]>([])
  useEffect(() => {
    setVoci([])
    if (!casellaId) return
    let vivo = true
    posta.leggiRubrica(casellaId).then((r) => { if (vivo) setVoci(posta.suggerimentiDa(r)) }).catch(() => undefined)
    return () => { vivo = false }
  }, [casellaId])
  return voci
}

/** Il destinatario che si sta scrivendo: dall'ultimo separatore prima del cursore al cursore. */
function inScrittura(el: HTMLInputElement): { inizio: number; scritto: string } {
  const cursore = el.selectionEnd ?? el.value.length
  const prima = el.value.slice(0, cursore)
  const inizio = Math.max(prima.lastIndexOf(','), prima.lastIndexOf(';')) + 1
  return { inizio, scritto: prima.slice(inizio).trimStart() }
}

export function SuggerimentiIndirizzi({ campo, cambia, voci, children }: {
  campo: RefObject<HTMLInputElement | null>
  cambia: (nuovo: string) => void
  voci: posta.Suggerimento[]
  children: ReactNode
}) {
  const idElenco = useId()
  const [proposte, setProposte] = useState<posta.Suggerimento[]>([])
  const [attivo, setAttivo] = useState(0)
  const aperto = proposte.length > 0

  /** Il destinatario scelto prende il posto di quello che si sta scrivendo, seguito da una virgola. */
  function scegli(v: posta.Suggerimento) {
    const el = campo.current
    if (!el) return
    const { inizio } = inScrittura(el)
    const testa = el.value.slice(0, inizio)
    const dopo = el.value.slice(el.selectionEnd ?? el.value.length)
    // Il resto del destinatario dopo il cursore se ne va con lui; gli altri restano.
    const separatore = dopo.search(/[,;]/)
    const coda = separatore >= 0 ? dopo.slice(separatore) : ''
    // Un gruppo mette i suoi indirizzi, senza quelli gia' nel campo.
    const presenti = new Set(el.value.split(/[,;]/).map((t) => posta.soloIndirizzo(t).toLowerCase()).filter(Boolean))
    const scelta = v.gruppo
      ? v.gruppo.voci.filter((x) => !presenti.has(posta.soloIndirizzo(x).toLowerCase())).join(', ')
      : posta.voceDestinatario(v)
    const voce = `${testa.trim() ? ' ' : ''}${scelta}`
    const nuovo = coda ? `${testa}${voce}${coda}` : `${testa}${voce}, `
    const cursore = coda ? testa.length + voce.length : nuovo.length
    el.value = nuovo
    el.setSelectionRange(cursore, cursore)
    cambia(nuovo)
    setProposte([])
  }

  useEffect(() => {
    const el = campo.current
    if (!el) return
    el.setAttribute('role', 'combobox')
    el.setAttribute('aria-autocomplete', 'list')
    el.setAttribute('aria-controls', idElenco)
    el.setAttribute('aria-expanded', String(aperto))
    if (aperto) el.setAttribute('aria-activedescendant', `${idElenco}-${attivo}`)
    else el.removeAttribute('aria-activedescendant')
    // Il browser proporrebbe i suoi indirizzi sopra i nostri.
    el.setAttribute('autocomplete', 'off')

    const scrive = () => {
      const { scritto } = inScrittura(el)
      // Gli indirizzi gia' nel campo non si ripropongono.
      const presenti = new Set(el.value.split(/[,;]/).map((t) => posta.soloIndirizzo(t).toLowerCase()).filter(Boolean))
      setProposte(!scritto ? []
        : posta.filtraSuggerimenti(voci.filter((v) => !presenti.has(v.indirizzo)), scritto))
      setAttivo(0)
    }
    const tasto = (e: KeyboardEvent) => {
      if (!aperto) return
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const passo = e.key === 'ArrowDown' ? 1 : -1
        setAttivo((a) => (a + passo + proposte.length) % proposte.length)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        scegli(proposte[attivo]!)
      } else if (e.key === 'Escape') {
        // Esc chiude i suggerimenti, non la finestra che contiene il compositore.
        e.preventDefault()
        e.stopPropagation()
        setProposte([])
      }
    }
    const esce = () => {
      setProposte([])
      const espanso = posta.espandiGruppi(el.value, voci)
      if (espanso !== null) { el.value = espanso; cambia(espanso) }
    }
    el.addEventListener('input', scrive)
    el.addEventListener('keydown', tasto)
    el.addEventListener('blur', esce)
    return () => {
      el.removeEventListener('input', scrive)
      el.removeEventListener('keydown', tasto)
      el.removeEventListener('blur', esce)
    }
    // scegli legge il campo al momento della scelta: non serve tra le dipendenze.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campo, idElenco, aperto, attivo, proposte, voci])

  return (
    <div className="suggerimenti-campo">
      {children}
      {aperto && (
        <ul className="suggerimenti-elenco" role="listbox" id={idElenco} aria-label="Suggerimenti dalla rubrica">
          {proposte.map((v, i) => (
            <li key={v.gruppo ? `gruppo-${v.gruppo.id}` : v.indirizzo} id={`${idElenco}-${i}`} role="option"
                aria-selected={i === attivo} className={i === attivo ? 'attivo' : ''}
                // Il clic non deve togliere il fuoco al campo prima della scelta.
                onMouseDown={(e) => e.preventDefault()} onClick={() => scegli(v)}>
              <span className="suggerimenti-nome">{v.nome ?? v.indirizzo}</span>
              {v.gruppo
                ? <span className="suggerimenti-indirizzo">{v.gruppo.voci.length === 1 ? '1 indirizzo' : `${v.gruppo.voci.length} indirizzi`}</span>
                : v.nome && <span className="suggerimenti-indirizzo">{v.indirizzo}</span>}
              {v.collega && <span className="suggerimenti-nota">collega</span>}
              {v.gruppo && <span className="suggerimenti-nota">gruppo</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
